// Deterministic round simulation.
//
// The sim advances in fixed 1/60 s ticks behind an accumulator, so the same
// level plus the same sequence of step()/tap calls always produces the same
// score on every device. No Math.random, no Date, no DOM. The UI reads
// `sim.state` each frame and drains `consumeEvents()` for effects.

const TICK_MS = 1000 / 60;
const BULB_POINTS = 10;
const HOME_POINTS = 100;
const STAR_POINTS = 50;
const BELL_POINTS = 500;
const MAX_MULTIPLIER = 5;
const METER_STEPS = 3;
const BURST_RADIUS = 140;
const BAUBLE_BOOST_MUL = 1.4;
const BAUBLE_BOOST_MS = 3000;
const CLONE_MS = 8000;
const TANGLED_SPEED = 0.6;
const PRACTICE_SPEED = 0.65;
const BELL_SPREAD = 0.3;
const MAX_HOPS_PER_TICK = 16;

export const SIM_CONSTANTS = Object.freeze({
  TICK_MS, BULB_POINTS, HOME_POINTS, STAR_POINTS, BELL_POINTS, MAX_MULTIPLIER, METER_STEPS,
  BURST_RADIUS, BAUBLE_BOOST_MUL, BAUBLE_BOOST_MS, CLONE_MS, TANGLED_SPEED, PRACTICE_SPEED, BELL_SPREAD,
});

function bulbT(edge, index) {
  return (index + 1) / (edge.bulbs.length + 1);
}

function dist2(ax, ay, bx, by) {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

export function createSim(level, { practice = false } = {}) {
  const nodeById = new Map(level.nodes.map((n) => [n.id, n]));
  const edgeById = new Map(level.edges.map((e) => [e.id, e]));
  const homeById = new Map(level.homes.map((h) => [h.id, h]));
  const bulbsTotal = level.edges.reduce((s, e) => s + e.bulbs.length, 0);
  const baseSpeed = level.sparkSpeed * (practice ? PRACTICE_SPEED : 1);

  // Homes touch when their cables share a node; a lonely home borrows its nearest neighbour.
  const homeNodes = new Map(
    level.homes.map((h) => [h.id, new Set(h.edgeIds.flatMap((id) => [edgeById.get(id).from, edgeById.get(id).to]))]),
  );
  const adjacency = new Map();
  for (const home of level.homes) {
    const mine = homeNodes.get(home.id);
    let list = level.homes.filter((o) => o !== home && [...homeNodes.get(o.id)].some((n) => mine.has(n))).map((o) => o.id);
    if (list.length === 0 && level.homes.length > 1) {
      const nearest = level.homes
        .filter((o) => o !== home)
        .sort((a, b) => dist2(a.x, a.y, home.x, home.y) - dist2(b.x, b.y, home.x, home.y) || a.id.localeCompare(b.id))[0];
      list = [nearest.id];
    }
    adjacency.set(home.id, list);
  }

  const switches = {};
  for (const node of level.nodes) switches[node.id] = node.switchIndex || 0;
  const homeRemaining = new Map(level.homes.map((h) => [h.id, h.bulbTotal]));
  const completed = new Set();
  let meterSteps = 0;
  let ticks = 0;
  let accumulator = 0;
  let events = [];

  const startEdge = edgeById.get(level.start.edgeId);
  const startNode = nodeById.get(startEdge.from);

  const state = {
    timeLeftMs: level.durationMs,
    elapsedMs: 0,
    score: 0,
    multiplier: 1,
    bestMultiplier: 1,
    effectiveMultiplier: 1,
    meter: 0,
    finished: false,
    sparks: [makeSpark(startEdge.id, false)],
    litBulbs: new Set(),
    litCount: 0,
    completedHomes: [],
    homesTotal: level.homes.length,
    bulbsTotal,
    starsCollected: 0,
    collectedStars: new Set(),
    moments: [],
    switches,
  };
  state.sparks[0].x = startNode.x;
  state.sparks[0].y = startNode.y;

  function makeSpark(edgeId, isClone) {
    return { x: 0, y: 0, edgeId, t: 0, speedMul: 1, parked: false, isClone, boostMs: 0, lifeMs: isClone ? CLONE_MS : 0, parkedAt: null };
  }

  function effectiveMultiplier() {
    return state.multiplier * (state.sparks.length > 1 ? 2 : 1);
  }

  function emit(event) {
    events.push(event);
  }

  function updatePosition(spark) {
    const edge = edgeById.get(spark.edgeId);
    const a = nodeById.get(edge.from);
    const b = nodeById.get(edge.to);
    spark.x = a.x + (b.x - a.x) * spark.t;
    spark.y = a.y + (b.y - a.y) * spark.t;
  }

  function lightBulb(edge, index) {
    const key = `${edge.id}:${index}`;
    if (state.litBulbs.has(key)) return false;
    state.litBulbs.add(key);
    state.litCount++;
    const eff = effectiveMultiplier();
    const points = edge.tangled ? (BULB_POINTS * eff) / 2 : BULB_POINTS * eff;
    state.score += points;
    const bulb = edge.bulbs[index];
    emit({ type: 'bulb', edgeId: edge.id, index, x: bulb.x, y: bulb.y, points });
    if (edge.homeId) {
      const remaining = homeRemaining.get(edge.homeId) - 1;
      homeRemaining.set(edge.homeId, remaining);
      if (remaining <= 0 && !completed.has(edge.homeId)) completeHome(homeById.get(edge.homeId));
    }
    return true;
  }

  function unlitBulbsOf(home) {
    const list = [];
    for (const id of home.edgeIds) {
      const edge = edgeById.get(id);
      for (let i = 0; i < edge.bulbs.length; i++) {
        if (!state.litBulbs.has(`${edge.id}:${i}`)) list.push({ edge, index: i });
      }
    }
    return list;
  }

  function completeHome(home) {
    completed.add(home.id);
    const eff = effectiveMultiplier();
    const points = HOME_POINTS * eff;
    state.score += points;
    state.completedHomes.push(home.id);
    state.moments.push(home.moment);
    emit({ type: 'home', homeId: home.id, x: home.x, y: home.y, moment: home.moment, points, multiplier: eff });
    state.multiplier = Math.min(MAX_MULTIPLIER, state.multiplier + 1);
    state.bestMultiplier = Math.max(state.bestMultiplier, state.multiplier);
    if (home.community) ringBell(home);
  }

  function ringBell(home) {
    state.score += BELL_POINTS;
    const batches = [];
    for (const otherId of adjacency.get(home.id) || []) {
      if (completed.has(otherId)) continue;
      const unlit = unlitBulbsOf(homeById.get(otherId));
      if (unlit.length === 0) continue;
      unlit.sort((a, b) => {
        const da = dist2(a.edge.bulbs[a.index].x, a.edge.bulbs[a.index].y, home.x, home.y);
        const db = dist2(b.edge.bulbs[b.index].x, b.edge.bulbs[b.index].y, home.x, home.y);
        return da - db || a.edge.id.localeCompare(b.edge.id) || a.index - b.index;
      });
      const n = Math.max(1, Math.floor(unlit.length * BELL_SPREAD));
      batches.push(unlit.slice(0, n));
    }
    const spread = batches.reduce((s, b) => s + b.length, 0);
    emit({ type: 'bell', homeId: home.id, spread });
    for (const batch of batches) for (const { edge, index } of batch) lightBulb(edge, index);
  }

  function collectStar(edge) {
    state.collectedStars.add(edge.id);
    state.starsCollected++;
    state.score += STAR_POINTS;
    meterSteps = Math.min(METER_STEPS, meterSteps + 1);
    state.meter = meterSteps / METER_STEPS;
    emit({ type: 'star', x: edge.star.x, y: edge.star.y });
  }

  function passAlong(edge, t0, t1) {
    for (let i = 0; i < edge.bulbs.length; i++) {
      const bt = bulbT(edge, i);
      if (bt > t0 && bt <= t1) lightBulb(edge, i);
    }
    if (edge.star && !state.collectedStars.has(edge.id) && edge.star.t > t0 && edge.star.t <= t1) collectStar(edge);
  }

  function arrive(spark, edge) {
    const node = nodeById.get(edge.to);
    const sparkIndex = state.sparks.indexOf(spark);
    let nextId;
    if (node.kind === 'split') {
      if (state.sparks.length < 2 && node.out.length >= 2) {
        const clone = makeSpark(node.out[1], true);
        clone.x = node.x;
        clone.y = node.y;
        state.sparks.push(clone);
        emit({ type: 'split', nodeId: node.id });
      }
      nextId = node.out[0];
    } else if (node.kind === 'junction') {
      nextId = node.out[switches[node.id] % node.out.length];
      emit({ type: 'junction', nodeId: node.id, sparkIndex });
    } else {
      nextId = node.out[0];
    }
    const next = edgeById.get(nextId);
    if (edge.homeId && edge.homeId !== next.homeId && !completed.has(edge.homeId)) {
      state.multiplier = 1;
      emit({ type: 'multiplier-reset', homeId: edge.homeId });
    }
    spark.edgeId = nextId;
    spark.t = 0;
    if (node.kind === 'bauble') {
      spark.parked = true;
      spark.parkedAt = node.id;
      emit({ type: 'bauble-enter', nodeId: node.id });
    }
  }

  function moveSpark(spark) {
    if (spark.parked) return;
    let budget = (baseSpeed * spark.speedMul * TICK_MS) / 1000;
    if (spark.boostMs > 0) {
      spark.boostMs -= TICK_MS;
      if (spark.boostMs <= 0) {
        spark.boostMs = 0;
        spark.speedMul = 1;
      }
    }
    let hops = 0;
    while (budget > 0 && hops++ < MAX_HOPS_PER_TICK) {
      const edge = edgeById.get(spark.edgeId);
      const mul = edge.tangled ? TANGLED_SPEED : 1;
      const pos = spark.t * edge.length;
      const room = edge.length - pos;
      const move = budget * mul;
      if (move < room) {
        const t1 = (pos + move) / edge.length;
        passAlong(edge, spark.t, t1);
        spark.t = t1;
        budget = 0;
      } else {
        passAlong(edge, spark.t, 1);
        budget -= room / mul;
        arrive(spark, edge);
        if (spark.parked) budget = 0;
      }
    }
    updatePosition(spark);
  }

  function tick() {
    if (state.finished) return;
    ticks++;
    state.elapsedMs = (ticks * 1000) / 60;
    state.timeLeftMs = Math.max(0, level.durationMs - state.elapsedMs);
    const count = state.sparks.length;
    for (let i = 0; i < count; i++) moveSpark(state.sparks[i]);
    for (let i = state.sparks.length - 1; i >= 1; i--) {
      const clone = state.sparks[i];
      if (!clone.isClone) continue;
      clone.lifeMs -= TICK_MS;
      if (clone.lifeMs <= 0) {
        state.sparks.splice(i, 1);
        emit({ type: 'clone-end' });
      }
    }
    state.effectiveMultiplier = effectiveMultiplier();
    if (state.elapsedMs >= level.durationMs) {
      state.finished = true;
      state.timeLeftMs = 0;
      emit({ type: 'finish' });
    }
  }

  const sim = {
    level,
    state,
    step(dtMs) {
      if (state.finished) return;
      if (!(dtMs > 0)) return;
      accumulator += dtMs;
      while (accumulator >= TICK_MS && !state.finished) {
        accumulator -= TICK_MS;
        tick();
      }
    },
    tapJunction(nodeId) {
      const node = nodeById.get(nodeId);
      if (!node || node.kind !== 'junction' || node.out.length < 2) return false;
      switches[nodeId] = (switches[nodeId] + 1) % node.out.length;
      return true;
    },
    setSwitch(nodeId, index) {
      const node = nodeById.get(nodeId);
      if (!node || node.kind !== 'junction' || node.out.length < 2) return false;
      switches[nodeId] = ((index % node.out.length) + node.out.length) % node.out.length;
      return true;
    },
    switchIndex(nodeId) {
      return switches[nodeId] ?? 0;
    },
    hitTest(x, y, radius = 44) {
      let best = null;
      let bestD = radius * radius;
      for (const node of level.nodes) {
        if (node.kind !== 'junction' && node.kind !== 'bauble') continue;
        const d = dist2(node.x, node.y, x, y);
        if (d <= bestD) {
          bestD = d;
          best = node.id;
        }
      }
      return best;
    },
    release() {
      if (state.finished) return false;
      let any = false;
      for (const spark of state.sparks) {
        if (!spark.parked) continue;
        spark.parked = false;
        spark.speedMul = BAUBLE_BOOST_MUL;
        spark.boostMs = BAUBLE_BOOST_MS;
        emit({ type: 'bauble-release', nodeId: spark.parkedAt });
        spark.parkedAt = null;
        any = true;
      }
      return any;
    },
    burst() {
      if (state.finished || meterSteps < METER_STEPS) return false;
      const spark = state.sparks[0];
      const targets = [];
      for (const edge of level.edges) {
        for (let i = 0; i < edge.bulbs.length; i++) {
          if (state.litBulbs.has(`${edge.id}:${i}`)) continue;
          const b = edge.bulbs[i];
          if (dist2(b.x, b.y, spark.x, spark.y) <= BURST_RADIUS * BURST_RADIUS) targets.push({ edge, index: i });
        }
      }
      meterSteps = 0;
      state.meter = 0;
      emit({ type: 'burst', x: spark.x, y: spark.y, radius: BURST_RADIUS, bulbs: targets.length });
      for (const { edge, index } of targets) lightBulb(edge, index);
      return true;
    },
    approaching() {
      return state.sparks.map((spark, sparkIndex) => {
        const edge = edgeById.get(spark.edgeId);
        return { sparkIndex, nodeId: edge.to, distance: (1 - spark.t) * edge.length };
      });
    },
    consumeEvents() {
      const out = events;
      events = [];
      return out;
    },
    result() {
      return {
        seed: level.seed,
        score: state.score,
        homesLit: state.completedHomes.length,
        homesTotal: state.homesTotal,
        bulbsLit: state.litCount,
        bulbsTotal,
        bestMultiplier: state.bestMultiplier,
        moments: [...state.moments],
        starsCollected: state.starsCollected,
        durationMs: level.durationMs,
      };
    },
  };
  return sim;
}
